import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Divider, IconButton, ListItemButton, Stack, Typography } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonIcon from '@mui/icons-material/Person';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { usePetOwners } from '../../../hooks/patients/usePetOwners';
import { useDeletePetOwner } from '../../../hooks/patients/useDeletePetOwner';
import SearchBox from '../../common/forms/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PetOwnerView from './PetOwnerView';
import PetOwnerForm from '../forms/PetOwnerForm';
import { getPetOwnerById } from '../../../api/petOwnerService';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function PetOwners() {
    const { t } = useTranslation('common'); 
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        resetParent();
    };

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");

    const [selectedPetOwner, setSelectedPetOwner] = React.useState(null);

    const handleListItemClick = (petOwner) => {
        setViewOpen(true);
        setSelectedPetOwner(petOwner);
    };

    const { showSnackbar } = useSnackbar();

    const { petOwners, loading, loadMore, searchFunction, resetParent } = usePetOwners(open, 1, 10);

    const { remove } = useDeletePetOwner(resetParent);

    const handleAdd = () => {
        setSelectedPetOwner(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (petOwner) => {
        setSelectedPetOwner(petOwner);
        setFormMode("edit");
        setFormOpen(true);
    };

    const onSavePetOwnerSuccess = async (savedPetOwner) => {
        await resetParent();
        const updatedPetOwner = await getPetOwnerById(savedPetOwner.id);
        setSelectedPetOwner(updatedPetOwner);
        if (!viewOpen) setViewOpen(true);
    };

    const handleDelete = async (petOwner) => {
        try {
            await remove(petOwner.id);
            showSnackbar(t('pet_owners.messages.delete_success', { name: petOwner.first_name }), "success");
        } catch (err) {
            showSnackbar(t('pet_owners.messages.delete_fail', { name: petOwner.first_name }), "error");
        }
    };

    return (
        <React.Fragment>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
                <PeopleAltIcon fontSize='large' color='secondary'/>
            </ListItemIcon>
            <ListItemText 
                primary="Pet Owners and Pets"
                secondary="view and manage pet owners and their pets"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            <Stack spacing={2}>
                <Typography variant="h6">Pet Owners and Pets</Typography>
                <SearchBox searchFunction={searchFunction} resetParent={resetParent} />
                <Divider />
            </Stack>
            </DialogTitle>

            <DialogContent>
                <>
                <List sx={{ minHeight: 500 }}>
                    {petOwners?.data?.map((petOwner) => (
                    <ListItem
                        key={petOwner.id}
                        secondaryAction={
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                            <VerticalHamburgerMenu
                            options={[
                                { 
                                    id: 1, 
                                    name: 'View',
                                    callback: () => handleListItemClick(petOwner),
                                    disabled: !userHasAccess(SUB_MODULES.PET_OWNERS, 1)
                                },
                                { 
                                    id: 2, 
                                    name: 'Edit',
                                    callback: () => handleEdit(petOwner),
                                    disabled: !userHasAccess(SUB_MODULES.PET_OWNERS, 2)
                                },
                                {
                                    id: 3,
                                    name: 'Delete',
                                    callback: () => handleDelete(petOwner),
                                    confirmMessage: t('pet_owners.messages.delete_confirm', { name: `${petOwner.first_name} ${petOwner.last_name}` }),
                                    disabled: !userHasAccess(SUB_MODULES.PET_OWNERS, 3)
                                },
                            ]}
                            />
                        </Box>
                        }
                    >
                        <ListItemButton onClick={() => {if (userHasAccess(SUB_MODULES.PET_OWNERS, 1)) handleListItemClick(petOwner)}}>
                            <ListItemIcon>
                                <PersonIcon fontSize='large' color='primary' />
                            </ListItemIcon>
                            <ListItemText
                            primary={`${petOwner.first_name} ${petOwner.last_name}`}
                            secondary={petOwner.pets?.length ? petOwner.pets.map(p => p.name).join(", ") : "(No pets)"}
                            />
                        </ListItemButton>
                    </ListItem>
                    ))}
                </List>

                {/* Load More only shows if more pages exist */}
                {petOwners?.pagination?.page < petOwners?.pagination?.totalPages && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <IconButton 
                            onClick={loadMore}
                            sx={{
                                width: '100%',              // fill container width
                                justifyContent: 'center',   // center the icon horizontally
                                color: 'primary.main',      // use theme's primary color
                            }}
                        >
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Box>
                )}
                </>
            </DialogContent>
            <DialogActions>
              <RequirePermission
                subModuleName={SUB_MODULES.PET_OWNERS}
                minAccessLevel={2}
              >
                <Button onClick={handleAdd}>Add Pet Owner</Button>
              </RequirePermission>
                <div style={{flex: '1 0 0'}} />
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <PetOwnerView 
            handleEdit={handleEdit}
            setPetOwners={resetParent}
            petOwner={selectedPetOwner}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        <PetOwnerForm 
            successCallback={onSavePetOwnerSuccess}
            setPetOwners={resetParent} 
            petOwner={selectedPetOwner}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
        />
        </React.Fragment>
    );
}